import { ClickAwayListener, Grid, Menu, Paper, Popper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { OrderBook, Security } from '../../proto/generated/security';
import { memo, useCallback, useContext, useEffect, useState } from 'react';
import { getOnlyOrderBook } from '../../util/tradepanel';
import websocketActions from '../../actions/websocket.actions';
import { useDispatch } from 'react-redux';
import { WebSocketResponseMessage } from '../../proto/generated/websocket';
import { WebSocketContext } from '../webSocketConnection/WebSocket';

type Props = {
	anchorEl: HTMLElement | null;
	security: Security
	handleOrderBookPriceChange: (price: number) => void;
}

interface CustomOrderBookType {
	type: 'bid' | 'ask';
	price: number;
	quantity: number;
	numberOfOrders: number;
}

const OrderBookSelectionDialog = (props: Props) => {
	const { anchorEl, security, handleOrderBookPriceChange } = props;
	const ws = useContext(WebSocketContext)
	const dispatch = useDispatch()
	const [orderBookData, setOrderBookData] = useState<CustomOrderBookType[]>([])

	const handleFetchOrderBook = useCallback(async () => {
		if (security) {
			try {
				const data = await getOnlyOrderBook({ security: security })
				const orderBookMenu: CustomOrderBookType[] = []
				data.bids.forEach(b => {
					orderBookMenu.push({
						...b,
						type: 'bid'
					})
				})
				data.asks.forEach(a => {
					orderBookMenu.push({
						...a,
						type: 'ask'
					})
				})
				setOrderBookData(orderBookMenu)
				websocketActions.setWebsocketSubscribeList(dispatch, {
					orderBookSubscriptions: [
						{
							tag: 'orderBook',
							securities: [{
								exchange: security.exchange,
								code: security.code
							}],
							topBidAskOnly: false
						}
					]
				})
			} catch (e) {
				console.error(e)
			}
		}
	}, [dispatch, security])

	useEffect(() => {
		if (anchorEl) {
			handleFetchOrderBook();
		}
	}, [anchorEl, handleFetchOrderBook]);

	const onWebSocketMessageReceived = useCallback((data: Uint8Array) => {
		const responseData = WebSocketResponseMessage.decode(data)
		if (responseData.orderBookSubscriptionResponses.length > 0) {
			const orderBookUpdates = responseData.orderBookSubscriptionResponses.find((responses) => responses.tag === 'orderBook')
			if (!orderBookUpdates || orderBookUpdates.orderBooks.length < 1) {
				return
			}
			const updatedOrderBook = orderBookUpdates.orderBooks.find((orderBook) => orderBook.security?.code === security?.code)
			if (updatedOrderBook) {
				const orderBookMenu: CustomOrderBookType[] = []
				updatedOrderBook.bids.forEach(b => {
					orderBookMenu.push({
						...b,
						type: 'bid'
					})
				})
				updatedOrderBook.asks.forEach(a => {
					orderBookMenu.push({
						...a,
						type: 'ask'
					})
				})
				setOrderBookData(orderBookMenu)
			}
		}
	}, [security])

	useEffect(() => {
		if (ws !== null) {
			ws!.addListener(onWebSocketMessageReceived)
		}
		return () => {
			if (ws !== null) {
				ws!.removeListener(onWebSocketMessageReceived)
			}
		}
	}, [onWebSocketMessageReceived, ws])

	return (
		<Popper
			disablePortal
			open={Boolean(anchorEl)}
			anchorEl={anchorEl}
			placement="top"
			className='font-feature-num'
			sx={{
				zIndex: 99999
			}}
		>
		{
			orderBookData.length > 0 ?
			<Paper elevation={2} sx={{ padding: '8px 8px', maxWidth: '350px' }}>
				<Grid container spacing={2} >
					<Grid item xs={6}>
						<Table size="small" >
							<TableHead>
								<TableRow>
									<TableCell style={{ padding: '2px', borderColor: 'transparent' }} align={'left'}>
										<Typography variant={'caption'} sx={{
											color: (theme) => theme.palette.text.disabled
										}}>QTY</Typography>
									</TableCell>
									<TableCell style={{ padding: '2px', borderColor: 'transparent' }} align={'right'}>
										<Typography variant={'caption'} sx={{
											color: (theme) => theme.palette.text.disabled
										}}>BIDS</Typography>
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{orderBookData
									?.filter(o => o.type === 'bid')
									.map((bid, i) => (
										<TableRow
											key={`bid-${i}`}
											hover
											onMouseDown={(e) => e.preventDefault()}
											onClick={() => handleOrderBookPriceChange(bid.price/(security?.priceMultiplier ?? 100))}
											sx={{ cursor: 'pointer' }}
										>
											<TableCell sx={{ padding: '4px', fontSize: '12px', borderColor: 'transparent' }}>
												<span className="blue-text">
													{bid.quantity.toLocaleString('en-IN')}
												</span>
											</TableCell>
											<TableCell align="right" sx={{ padding: '4px', borderColor: 'transparent' }}>
												<span className="blue-text">
													{(bid.price / (security?.priceMultiplier ?? 100)).toFixed(2)}
												</span>
											</TableCell>
										</TableRow>
									))}
							</TableBody>
						</Table>
					</Grid>
					<Grid item xs={6}>
						<Table size="small" style={{ tableLayout: 'fixed' }}>
							<TableHead>
								<TableRow>
									<TableCell style={{ padding: '2px', borderColor: 'transparent' }} align={'left'}>
										<Typography sx={{
											color: (theme) => theme.palette.text.disabled
										}} variant={'caption'}>OFFERS</Typography></TableCell>
									<TableCell style={{ padding: '2px', borderColor: 'transparent' }} align={'right'}>
										<Typography sx={{
											color: (theme) => theme.palette.text.disabled
										}} variant={'caption'}>QTY</Typography></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{orderBookData
									?.filter(o => o.type === 'ask')
									.map((ask, i) => (
										<TableRow
											key={`ask-${i}`}
											hover
											onMouseDown={(e) => e.preventDefault()}
											onClick={() => handleOrderBookPriceChange(ask.price / (security?.priceMultiplier ?? 100))}
											sx={{ cursor: 'pointer' }}
										>
											<TableCell sx={{ padding: '4px', borderColor: 'transparent' }}>
												<span className="orange-text">
													{(ask.price / (security?.priceMultiplier ?? 100)).toFixed(2)}
												</span>
											</TableCell>
											<TableCell align="right" sx={{ padding: '4px', fontSize: '12px', borderColor: 'transparent' }}>
												<span className="orange-text">
													{ask.quantity.toLocaleString('en-IN')}
												</span>
											</TableCell>
										</TableRow>
									))}
							</TableBody>
						</Table>
					</Grid>
				</Grid>
			</Paper> : null
		}
		</Popper>
	)
}

export default OrderBookSelectionDialog